import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User as SupabaseUser, Session, AuthError } from '@supabase/supabase-js';
import { supabase, Database } from '../lib/supabase';
import { devLog } from '../lib/dev-logger';

// Extended User interface with profile data
export interface User extends SupabaseUser {
  profile?: Database['public']['Tables']['user_profiles']['Row'];
  name?: string; // Computed from profile.name or user_metadata.name
}

interface AuthContextType {
  user: User | null;
  isLoggedIn: boolean;
  loading: boolean;
  signUp: (email: string, password: string, userData: { name: string; phone?: string }) => Promise<{ user: SupabaseUser | null; error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ user: SupabaseUser | null; error: AuthError | null }>;
  signInWithGoogle: () => Promise<{ data: any; error: AuthError | null }>;
  signInWithMagicLink: (email: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  logout: () => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: Database['public']['Tables']['user_profiles']['Update']) => Promise<{ error: any }>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a SupabaseAuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const SupabaseAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Load user profile data
  const loadUserProfile = async (supabaseUser: SupabaseUser): Promise<User> => {
    try {
      // Diagnostic fix: add timeout protection
      const profileQuery = supabase
        .from('user_profiles')
        .select('*')
        .eq('id', supabaseUser.id)
        .single();
        
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Profile query timeout')), 1000); // Shortened to 1s
      });

      const { data: profile, error } = await Promise.race([profileQuery, timeoutPromise]) as any;

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Profile load error:', error);
      }

      return {
        ...supabaseUser,
        profile: profile || undefined,
        name: profile?.name || supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || 'User'
      };
    } catch (error) {
      console.warn('Profile load failed (using default profile):', error instanceof Error ? error.message : error);
      return {
        ...supabaseUser,
        profile: undefined,
        name: supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || 'User'
      };
    }
  };

  // Supabase recommended approach: simple auth logic using only onAuthStateChange
  // INITIAL_SESSION-based single bootstrap (fixes infinite loading)
  useEffect(() => {
    let mounted = true
    let bootstrapped = false

    // Safety net: force loading release if INITIAL_SESSION not received within 3s
    const fallbackTimer = setTimeout(() => {
      if (mounted && !bootstrapped) {
        console.warn('[AUTH] Auth initialization timeout (3s) - forcing loading release')
        setLoading(false)
        bootstrapped = true
      }
    }, 3000)

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event: any, session: any) => {
      if (!mounted) return
      devLog.info(`[AUTH_CONTEXT] Event: ${event}`, session?.user?.email || 'no user')

      if (event === 'INITIAL_SESSION') {
        clearTimeout(fallbackTimer) // Normal response - cancel timer
        if (session?.user) {
          try {
            const u = await loadUserProfile(session.user)
            setUser(u)
          } catch (error) {
            devLog.error('Profile load failed:', error)
            setUser(session.user as any)
          }
        } else {
          setUser(null)
        }
        setLoading(false)  // Must release loading only here
        bootstrapped = true
        devLog.info(`[AUTH_CONTEXT] INITIAL_SESSION processed. Loading complete.`)
      }

      if (event === 'SIGNED_IN' && session) {
        // Step 1: Load profile + release loading immediately (minimize user wait)
        try {
          const u = await loadUserProfile(session.user)
          setUser(u)
        } catch (err) {
          devLog.error('SIGNED_IN profile load failed:', err)
          setUser(session.user as any)
        } finally {
          setLoading(false)  // Load profile only and release loading immediately
          clearTimeout(fallbackTimer)
          if (!bootstrapped) {
            bootstrapped = true
          }
        }

        // Step 2: Coupon issuance processed in background (no loading screen!)
        const pendingReferralCode = localStorage.getItem('pending_referral_code')
        const isNewUser = !localStorage.getItem(`oauth_processed_${session.user.id}`)

        if (isNewUser) {
          devLog.info('New OAuth signup detected, starting background coupon issuance')

          // Process async (no await = no user waiting)
          ;(async () => {
            try {
              // 1. Issue WELCOME10 coupon (idempotent — user may already have it from the email signup path)
              const { data: welcomeCoupon } = await supabase
                .from('coupons')
                .select('id')
                .eq('code', 'WELCOME10')
                .maybeSingle()

              if (welcomeCoupon) {
                await supabase
                  .from('user_coupons')
                  .upsert(
                    { user_id: session.user.id, coupon_id: welcomeCoupon.id },
                    { onConflict: 'user_id,coupon_id', ignoreDuplicates: true }
                  )
                devLog.info('WELCOME10 coupon ensured')
              }

              // 2. Process referral
              if (pendingReferralCode) {
                devLog.info('Processing referral after OAuth signup:', pendingReferralCode)
                const { referralApi } = await import('../lib/supabase-api')
                const signup = await referralApi.registerReferralSignup(pendingReferralCode, session.user.id)
                if (signup) {
                  devLog.info('OAuth referral processed + FRIENDNEW10K coupon issued!')
                  localStorage.removeItem('pending_referral_code')
                } else {
                  devLog.error('OAuth referral processing failed')
                }
              }

              // Save processing complete flag
              localStorage.setItem(`oauth_processed_${session.user.id}`, 'true')
            } catch (couponError) {
              devLog.error('Background coupon issuance failed:', couponError)
            }
          })()
        }
      }

      if (event === 'SIGNED_OUT') {
        setUser(null)
        setLoading(false)  // Always release loading (diagnostic fix)
        if (!bootstrapped) {
          bootstrapped = true
        }
      }
    })

    return () => {
      mounted = false
      clearTimeout(fallbackTimer)
      subscription.unsubscribe()
      devLog.info('[AUTH_CONTEXT] Auth listener unsubscribed.')
    }
  }, [])  // Truly runs only once

  const signUp = async (email: string, password: string, userData: { name: string; phone?: string }) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData.name,
            phone: userData.phone
          },
          // Disable email confirmation in development
          emailRedirectTo: undefined,
          captchaToken: undefined
        }
      });

      if (data.user && !error) {
        // Create user profile (trigger handles this automatically, but as a fallback).
        // Schema: grade CHECK IN ('USER','ADMIN','SUPER_ADMIN') — 'BASIC' 은 위반이었음.
        // points 컬럼은 user_profiles 에 없음 (user_points 테이블이 따로 있음).
        // 이 두 실수로 upsert 가 항상 fail → 최근 신규 가입자 대부분이 orphan 상태였음.
        try {
          const { error: profileError } = await supabase
            .from('user_profiles')
            .upsert({
              id: data.user.id,
              name: userData.name,
              phone: userData.phone || null,
              username: null, // username can be set later in MyPage
              grade: 'USER'
            });

          if (profileError) {
            devLog.error('Profile creation error:', profileError);
          }
        } catch (profileError) {
          devLog.error('Profile creation failed:', profileError);
        }
      }

      // onAuthStateChange handles all state changes (including loading)
      return { user: data.user, error };
    } catch (error) {
      return { user: null, error: error as AuthError };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      // onAuthStateChange handles all state changes (including loading)
      return { user: data.user, error };
    } catch (error) {
      return { user: null, error: error as AuthError };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      // OAuth automatically redirects
      // onAuthStateChange handles state changes after callback
      return { data, error };
    } catch (error) {
      devLog.error('Google sign-in error:', error);
      return { data: null, error: error as AuthError };
    }
  };

  const signInWithMagicLink = async (email: string) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      return { error };
    } catch (error) {
      devLog.error('Magic link error:', error);
      return { error: error as AuthError };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      // onAuthStateChange handles all state changes (including loading)
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const updateProfile = async (updates: Database['public']['Tables']['user_profiles']['Update']) => {
    if (!user) {
      return { error: new Error('User is not logged in.') };
    }

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id);

      if (!error) {
        // Refresh profile
        await refreshProfile();
      }

      return { error };
    } catch (error) {
      return { error };
    }
  };

  const refreshProfile = async () => {
    if (!user) return;

    try {
      const userWithProfile = await loadUserProfile(user);
      setUser(userWithProfile);
    } catch (error) {
      devLog.error('Profile refresh failed:', error);
    }
  };

  const value = {
    user,
    isLoggedIn: !!user,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithMagicLink,
    signOut,
    logout: signOut, // Alias for backward compatibility
    updateProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};